'use client';

import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useTranslation } from 'react-i18next';

import { MotivoChip } from '@/app/fila/components/MotivoChip';
import { SectionTitle } from '@/components/layout/SectionTitle';
import { SURFACE_CARD, SURFACE_INSET } from '@/components/surface/surfaces';
import { FilaItem } from '@/domain/fila/interfaces/fila';
import { cn } from '@/lib/utils';

import { fatosDoItem } from './homeFatosDoDia';

const FILA_PATH = '/fila';

/** What reads as "today" on one screen; the queue itself is one click away. */
const ITENS_DO_DIA = 5;

interface HomeFilaPreviewProps {
  /** The whole queue, already in the priority order the API resolved. */
  itens: Array<FilaItem>;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/** The first open items of the attention queue: what to do today, in the queue's own order. */
export function HomeFilaPreview({ itens, loading, failed, onRetry, className }: HomeFilaPreviewProps) {
  const { t } = useTranslation('common');

  // The queue screen opens on the unresolved items: the day's guide starts from the same list.
  const doDia = itens.filter((item) => !item.resolvido).slice(0, ITENS_DO_DIA);

  return (
    <section className={cn(SURFACE_CARD, 'flex flex-col gap-2.5 p-4', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <SectionTitle>{t('home.hoje.titulo')}</SectionTitle>
        <span className="text-xs text-foreground-subtle">{t('home.hoje.subtitulo', { n: ITENS_DO_DIA })}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size={6} color="foreground-faint" />
        </div>
      ) : failed ? (
        <NoDataFound
          variant="error"
          size="sm"
          title={t('home.erroTitulo')}
          description={t('home.hoje.erroDescricao')}
          action={{ label: t('home.tentar'), onClick: onRetry, icon: 'refresh' }}
        />
      ) : doDia.length === 0 ? (
        <NoDataFound
          variant="success"
          size="sm"
          icon="check_circle"
          title={t('home.hoje.vazioTitulo')}
          description={t('home.hoje.vazioDescricao')}
        />
      ) : (
        <ul className="flex animate-fade-in flex-col gap-1.5">
          {doDia.map((item) => {
            const fatos = fatosDoItem(t, item);
            const motivo = <MotivoChip tipo={item.tipo} label={t(`fila.tipo.${item.tipo}`)} />;
            /** A frase da API, que virou dica; ela pode não vir, e aí a etiqueta vai só. */
            const porque = (item.porque ?? '').trim();
            return (
              <li key={item.uid} className={cn(SURFACE_INSET, 'flex items-center gap-x-3 px-3 py-2')}>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {/* QUEM é: a OP e o produto juntos, com a etiqueta do motivo ao lado. */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-xs font-semibold text-foreground">
                      {t('fila.opLabel')} {item.opRaw}
                    </span>
                    {/* A long product wraps onto the next line instead of being cut off. */}
                    {item.produto !== '' && (
                      <>
                        <span className="text-xs text-foreground-faint" aria-hidden>
                          &middot;
                        </span>
                        <span className="min-w-0 break-words text-xs text-foreground-subtle">{item.produto}</span>
                      </>
                    )}
                    {/* A explicação longa do motivo é DICA, não corpo do cartão: ela repetia
                        a ação que a linha de baixo já dá, e cinco cartões assim viravam
                        parede de texto. Quem quiser o porquê inteiro passa o mouse. */}
                    {porque === '' ? motivo : <ControlledTooltip content={porque}>{motivo}</ControlledTooltip>}
                  </div>
                  {/* Os FATOS, curtos: datas e números, nunca a frase da API. */}
                  {fatos !== '' && <p className="text-xs leading-snug text-foreground-muted">{fatos}</p>}
                  {/* A AÇÃO sugerida, UMA vez só: a lista chama "O que fazer hoje", e dizer
                      o que houve sem dizer o que fazer deixa a decisão inteira para quem lê.
                      O verbo é o mesmo que a Fila oferece na linha. */}
                  <p className="flex items-center gap-1 text-xs font-medium text-foreground-muted">
                    <MaterialSymbol icon="arrow_forward" size={12} className="shrink-0" aria-hidden />
                    {t(`home.hoje.acao.${item.tipo}`)}
                  </p>
                </div>

                <LinkButton
                  href={FILA_PATH}
                  variant="outlined"
                  tone="neutral"
                  size="sm"
                  className="shrink-0 normal-case"
                >
                  {t('home.hoje.verNaFila')}
                  <MaterialSymbol icon="chevron_right" size={16} />
                </LinkButton>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
